import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./PracticeDetailsPage.module.scss";

type AddressForm = {
  name: string;
  email: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  postcode: string;
  contact_no: string;
};
type SiteForm = AddressForm & { key: string; display_name: string };
type ClinicianForm = {
  name: string;
  email: string;
  role: string;
  siteKey: string;
  level: string;
  mobile_phone: string;
  mobile_phone_country: string;
  gdc_number: string;
  nhs_number: string;
};
type ApiRecord = Record<string, unknown>;

const createAddress = (): AddressForm => ({
  name: "",
  email: "",
  address_line_1: "",
  address_line_2: "",
  town: "",
  postcode: "",
  contact_no: "",
});

function nonEmptyStrings(values: Record<string, string | undefined>) {
  return Object.entries(values).reduce<Record<string, string>>((result, [key, value]) => {
    if (value?.trim()) result[key] = value.trim();
    return result;
  }, {});
}

function asRecord(value: unknown): ApiRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as ApiRecord
    : {};
}

function unwrapResponse(value: unknown): unknown {
  return asRecord(value).data ?? value;
}

function recordId(value: unknown, key: "practice_id" | "site_id"): number | null {
  const record = asRecord(value);
  const raw = record.id ?? record[key];
  const id = typeof raw === "string" && /^\d+$/.test(raw) ? Number(raw) : raw;
  return typeof id === "number" && Number.isSafeInteger(id) && id > 0 ? id : null;
}

function createdSites(value: unknown): ApiRecord[] {
  const response = unwrapResponse(value);
  const sites = asRecord(response).sites ?? asRecord(response).site ?? response;
  return Array.isArray(sites) ? sites.map(asRecord) : [asRecord(sites)];
}

function requestHeaders() {
  const auth = axios.defaults.headers.common.Authorization
    ?? axios.defaults.headers.common.authorization
    ?? Cookies.get("token");
  if (!auth || !String(auth).trim()) {
    throw new Error("Your session has expired. Please sign in again.");
  }
  const token = String(auth).trim().replace(/^Bearer\s+/i, "").trim();
  if (!token || token === "undefined" || token === "null") {
    throw new Error("Your session has expired. Please sign in again.");
  }
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

function onboardingUrl(endpoint: string) {
  const base = process.env.REACT_APP_BASE_URL_V2;
  if (!base) throw new Error("Practice setup is unavailable. Please try again later.");
  return `${base.replace(/\/+$/, "")}/onboarding/${endpoint}`;
}

function errorMessage(error: unknown, stage: string) {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    const reason = typeof detail === "string"
      ? detail
      : Array.isArray(detail)
        ? detail.map((item) => {
          const field = asRecord(item);
          const location = Array.isArray(field.loc) ? field.loc.filter((part) => part !== "body").join(".") : "";
          return `${location ? `${location}: ` : ""}${field.msg || "Invalid field"}`;
        }).join("; ")
        : "Please try again.";
    return `Could not create ${stage}. ${reason}`;
  }
  return error instanceof Error ? error.message : `Could not create ${stage}. Please try again.`;
}

type TextFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  pattern?: string;
  min?: number;
  max?: number;
  hint?: string;
};

function TextField({ id, label, value, onChange, hint, ...inputProps }: TextFieldProps) {
  return (
    <div className={styles.fieldCard}>
      <label className={styles.label} htmlFor={id}>
        {label}{inputProps.required && <span className={styles.requiredMark}> *</span>}
      </label>
      <input
        {...inputProps}
        id={id}
        name={id}
        className={styles.input}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      {hint && <span id={`${id}-hint`} className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}

function SectionHeading({ id, title, expanded, onToggle, level = 2 }: {
  id: string;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  level?: 2 | 3;
}) {
  const Heading = level === 3 ? "h3" : "h2";
  return (
    <Heading id={`${id}-heading`} className={level === 2 ? styles.cardTitle : undefined}>
      <button type="button" className={styles.collapseToggle} onClick={onToggle}
        aria-expanded={expanded} aria-controls={`${id}-content`}>
        {title}
        <svg className={`${styles.collapseChevron} ${expanded ? styles.isExpanded : ""}`}
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
    </Heading>
  );
}

function AddressFields({
  prefix,
  value,
  onChange,
  emailRequired = false,
}: {
  prefix: string;
  value: AddressForm;
  onChange: (value: AddressForm) => void;
  emailRequired?: boolean;
}) {
  const fields: { key: keyof AddressForm; label: string; required?: boolean; type?: string }[] = [
    { key: "name", label: "Name", required: true },
    { key: "email", label: "Email", type: "email", required: emailRequired },
    { key: "address_line_1", label: "Address line 1", required: true },
    { key: "address_line_2", label: "Address line 2" },
    { key: "town", label: "Town", required: true },
    { key: "postcode", label: "Postcode", required: true },
    { key: "contact_no", label: "Contact number", type: "tel" },
  ];
  return (
    <>
      {fields.map(({ key, ...field }) => (
        <TextField
          key={key}
          {...field}
          id={`${prefix}-${key}`}
          value={value[key]}
          onChange={(next) => onChange({ ...value, [key]: next })}
        />
      ))}
    </>
  );
}

export default function NonDentallyPracticeForm({ onComplete, onBack }: {
  onComplete: () => void;
  onBack: () => void;
}) {
  const [practice, setPractice] = useState(createAddress);
  const [paymentPlanId, setPaymentPlanId] = useState("");
  const [sites, setSites] = useState<SiteForm[]>(() => [{ ...createAddress(), key: "site-1", display_name: "" }]);
  const [clinician, setClinician] = useState<ClinicianForm>({
    name: "", email: "", role: "", siteKey: "", level: "",
    mobile_phone: "", mobile_phone_country: "", gdc_number: "", nhs_number: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  const [practiceSaved, setPracticeSaved] = useState(false);
  const [sitesSaved, setSitesSaved] = useState(false);
  const [expanded, setExpanded] = useState({ practice: true, sites: true, clinician: true });
  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({ "site-1": true });
  const invalidField = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const nextSiteKey = useRef(2);
  const submitting = useRef(false);
  // Keep successful results so a later failure can be retried without duplicating records.
  const practiceResponse = useRef<unknown>(undefined);
  const sitesResponse = useRef<unknown>(undefined);

  useEffect(() => {
    if (invalidField.current) {
      const field = invalidField.current;
      invalidField.current = null;
      field.focus();
      field.reportValidity();
    }
  }, [expanded, expandedSites]);

  function toggleSection(section: keyof typeof expanded) {
    setExpanded((current) => ({ ...current, [section]: !current[section] }));
  }

  function revealInvalidField(field: HTMLInputElement | HTMLSelectElement) {
    invalidField.current = field;
    if (field.id.startsWith("practice-")) {
      setExpanded((current) => ({ ...current, practice: true }));
    } else if (field.id.startsWith("clinician-")) {
      setExpanded((current) => ({ ...current, clinician: true }));
    } else {
      const site = sites.find(({ key }) => field.id.startsWith(`${key}-`));
      setExpanded((current) => ({ ...current, sites: true }));
      if (site) setExpandedSites((current) => ({ ...current, [site.key]: true }));
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    if (!event.currentTarget.checkValidity()) {
      const field = event.currentTarget.querySelector<HTMLInputElement | HTMLSelectElement>("input:invalid, select:invalid");
      if (field) revealInvalidField(field);
      return;
    }
    setError("");

    const requiredPractice = [practice.name, practice.email, practice.address_line_1, practice.town, practice.postcode];
    const requiredSites = sites.flatMap((site) => [site.name, site.display_name, site.address_line_1, site.town, site.postcode]);
    if ([...requiredPractice, ...requiredSites, clinician.name, clinician.email, clinician.role].some((value) => !value.trim())
      || !clinician.siteKey || !clinician.level) {
      setError("Please complete all required practice, site and clinician fields.");
      return;
    }
    if (paymentPlanId && (!Number.isSafeInteger(Number(paymentPlanId)) || Number(paymentPlanId) < 1)) {
      setError("NHS payment plan ID must be a positive whole number.");
      return;
    }
    if (clinician.mobile_phone && (!/^\d+$/.test(clinician.mobile_phone) || !Number.isSafeInteger(Number(clinician.mobile_phone)))) {
      setError("Clinician mobile phone must contain digits only and be a valid whole number.");
      return;
    }

    submitting.current = true;
    setIsSubmitting(true);
    let currentStage = "practice";
    try {
      const headers = requestHeaders();
      setStage("practice");
      if (practiceResponse.current === undefined) {
        const response = await axios.post(onboardingUrl("create_non_dentally_practice"), {
          ...nonEmptyStrings(practice),
          ...(paymentPlanId ? { nhs_payment_plan_id: Number(paymentPlanId) } : {}),
        }, { headers });
        practiceResponse.current = response.data ?? null;
        setPracticeSaved(true);
      }
      const result = asRecord(unwrapResponse(practiceResponse.current));
      const practiceId = recordId(result.practice ?? result, "practice_id");
      if (!practiceId) {
        throw new Error("The practice was saved, but the server did not return a valid practice ID. Please contact support.");
      }

      currentStage = "sites";
      setStage("sites");
      if (sitesResponse.current === undefined) {
        const response = await axios.post(onboardingUrl(`create_non_dentally_sites/${practiceId}`),
          sites.map(({ key, display_name, ...site }) => ({ ...nonEmptyStrings(site), display_name: display_name.trim() })),
          { headers });
        sitesResponse.current = response.data ?? null;
        setSitesSaved(true);
      }
      const siteRecords = createdSites(sitesResponse.current);
      if (siteRecords.length !== sites.length || siteRecords.some((site) => !recordId(site, "site_id"))) {
        throw new Error("The sites were saved, but the server did not return their IDs. Please contact support.");
      }
      const selectedIndex = sites.findIndex((site) => site.key === clinician.siteKey);
      const selectedSite = sites[selectedIndex];
      // Prefer matching names if the API returns the created sites in a different order.
      const matchingSites = selectedSite ? siteRecords.filter((site) =>
        site.name === selectedSite.name.trim() && site.display_name === selectedSite.display_name.trim()
      ) : [];
      const siteId = clinician.siteKey === "primary"
        ? recordId(result.primary_site, "site_id")
        : recordId(matchingSites.length === 1 ? matchingSites[0] : siteRecords[selectedIndex], "site_id");
      if (!siteId) {
        throw new Error("The selected site has no valid site ID. Please select another site or contact support.");
      }

      currentStage = "clinician";
      setStage("clinician");
      const { siteKey, mobile_phone, ...clinicianFields } = clinician;
      await axios.post(onboardingUrl(`create_non_dentally_clinician/${practiceId}`), {
        ...nonEmptyStrings(clinicianFields),
        site_id: siteId,
        ...(mobile_phone ? { mobile_phone: Number(mobile_phone) } : {}),
      }, { headers });
      onComplete();
    } catch (err) {
      setError(errorMessage(err, currentStage));
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  }

  function updateSite(key: string, values: Partial<SiteForm>) {
    setSites((current) => current.map((site) => site.key === key ? { ...site, ...values } : site));
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Non-Dentally practice setup" aria-busy={isSubmitting}>
      <section className={styles.card} aria-labelledby="practice-heading">
        <div className={`${styles.cardHeader} ${expanded.practice ? "" : styles.collapsedHeader}`}>
          <div className={styles.collapseHeaderContent}>
            <SectionHeading id="practice" title="Practice information" expanded={expanded.practice}
              onToggle={() => toggleSection("practice")} />
            <p className={styles.cardSubtitle}>
              {practiceSaved ? "Practice saved. Continue setting up your sites and clinician." : "Enter your practice details. A primary site will also be created. Fields marked * are required."}
            </p>
          </div>
        </div>
        <div id="practice-content" hidden={!expanded.practice}>
          <fieldset className={styles.formFields} disabled={isSubmitting || practiceSaved} aria-labelledby="practice-heading">
            <div className={styles.grid}>
              <AddressFields prefix="practice" value={practice} onChange={setPractice} emailRequired />
              <TextField id="practice-nhs-payment-plan" label="NHS payment plan ID" type="number" min={1} max={Number.MAX_SAFE_INTEGER}
                value={paymentPlanId} onChange={setPaymentPlanId} />
            </div>
          </fieldset>
        </div>
      </section>

      <section className={styles.card} aria-labelledby="sites-heading">
        <div className={`${styles.cardHeader} ${expanded.sites ? "" : styles.collapsedHeader}`}>
          <div className={styles.collapseHeaderContent}>
            <SectionHeading id="sites" title="Sites" expanded={expanded.sites}
              onToggle={() => toggleSection("sites")} />
            <p className={styles.cardSubtitle}>
              {sitesSaved ? "Sites saved. Continue setting up your clinician." : "Add one or more sites for your practice, in addition to the primary site."}
            </p>
          </div>
          <button type="button" className={styles.btnSecondary} disabled={isSubmitting || sitesSaved} onClick={() => {
            const key = `site-${nextSiteKey.current++}`;
            setSites((current) => [...current, { ...createAddress(), key, display_name: "" }]);
            setExpanded((current) => ({ ...current, sites: true }));
            setExpandedSites((current) => ({ ...current, [key]: true }));
          }}>Add site</button>
        </div>
        <div id="sites-content" hidden={!expanded.sites}>
          {sites.map((site, index) => (
            <div key={site.key} className={styles.siteCard} role="group" aria-labelledby={`${site.key}-heading`}>
              <div className={`${styles.siteHeader} ${expandedSites[site.key] ? "" : styles.collapsedHeader}`}>
                <SectionHeading id={site.key} title={`Site ${index + 1}`} level={3} expanded={!!expandedSites[site.key]}
                  onToggle={() => setExpandedSites((current) => ({ ...current, [site.key]: !current[site.key] }))} />
                {sites.length > 1 && <button type="button" className={styles.removeSite} disabled={isSubmitting || sitesSaved}
                  aria-label={`Remove site ${index + 1}`} onClick={() => {
                  setSites((current) => current.filter((item) => item.key !== site.key));
                  if (clinician.siteKey === site.key) setClinician({ ...clinician, siteKey: "" });
                }}>Remove</button>}
              </div>
              <div id={`${site.key}-content`} hidden={!expandedSites[site.key]}>
                <fieldset className={styles.formFields} disabled={isSubmitting || sitesSaved}>
                  <div className={styles.grid}>
                    <AddressFields prefix={site.key} value={site} onChange={(next) => updateSite(site.key, next)} />
                    <TextField id={`${site.key}-display-name`} label="Display name" value={site.display_name}
                      onChange={(next) => updateSite(site.key, { display_name: next })} required />
                  </div>
                </fieldset>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.card} aria-labelledby="clinician-heading">
        <div className={`${styles.cardHeader} ${expanded.clinician ? "" : styles.collapsedHeader}`}>
          <div className={styles.collapseHeaderContent}>
            <SectionHeading id="clinician" title="Clinician data" expanded={expanded.clinician}
              onToggle={() => toggleSection("clinician")} />
            <p className={styles.cardSubtitle}>Enter the clinician details and choose their site.</p>
          </div>
        </div>
        <div id="clinician-content" hidden={!expanded.clinician}>
          <fieldset className={styles.formFields} disabled={isSubmitting}>
            <div className={styles.grid}>
              <TextField id="clinician-name" label="Full name" value={clinician.name} required
                onChange={(name) => setClinician({ ...clinician, name })} />
              <TextField id="clinician-email" label="Email" type="email" value={clinician.email} required
                onChange={(email) => setClinician({ ...clinician, email })} />
              <TextField id="clinician-role" label="Role" value={clinician.role} required
                onChange={(role) => setClinician({ ...clinician, role })} />
              <div className={styles.fieldCard}>
                <label className={styles.label} htmlFor="clinician-site">Site<span className={styles.requiredMark}> *</span></label>
                <select id="clinician-site" name="clinician-site" className={styles.input} value={clinician.siteKey} required
                  onChange={(event) => setClinician({ ...clinician, siteKey: event.target.value })}>
                  <option value="">Select a site</option>
                  <option value="primary">Primary site{practice.name ? ` — ${practice.name}` : ""}</option>
                  {sites.map((site, index) => <option key={site.key} value={site.key}>
                    {site.display_name || site.name || `Site ${index + 1}`}
                  </option>)}
                </select>
              </div>
              <div className={styles.fieldCard}>
                <label className={styles.label} htmlFor="clinician-level">Level<span className={styles.requiredMark}> *</span></label>
                <select id="clinician-level" name="clinician-level" className={styles.input} value={clinician.level} required
                  onChange={(event) => setClinician({ ...clinician, level: event.target.value })}>
                  <option value="">Select a level</option>
                  <option value="Practice Owner">Practice Owner</option>
                  <option value="Associate Dentist">Associate Dentist</option>
                </select>
              </div>
              <TextField id="clinician-mobile" label="Mobile phone" type="tel" pattern="[0-9]+" value={clinician.mobile_phone}
                hint="Enter digits only; add the country separately."
                onChange={(mobile_phone) => setClinician({ ...clinician, mobile_phone })} />
              <TextField id="clinician-mobile-country" label="Mobile phone country" value={clinician.mobile_phone_country}
                onChange={(mobile_phone_country) => setClinician({ ...clinician, mobile_phone_country })} />
              <TextField id="clinician-gdc" label="GDC number" value={clinician.gdc_number}
                onChange={(gdc_number) => setClinician({ ...clinician, gdc_number })} />
              <TextField id="clinician-nhs" label="NHS number" value={clinician.nhs_number}
                onChange={(nhs_number) => setClinician({ ...clinician, nhs_number })} />
            </div>
          </fieldset>
        </div>
      </section>

      {error && <p className={styles.errorMessage} role="alert">{error}</p>}
      <div className={styles.actions}>
        <button type="button" className={styles.btnSecondary} onClick={onBack} disabled={isSubmitting}>Back</button>
        <button type="submit" className={styles.btnPrimary} disabled={isSubmitting} aria-live="polite">
          {isSubmitting ? `Creating ${stage || "practice"}...` : "Next"}
        </button>
      </div>
    </form>
  );
}
